import { useEffect, useCallback } from "react";
import { MapContainer, Circle, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { AtlasLocation } from "@/hooks/useAtlasData";
import { SamsaraVehicle } from "@/hooks/useSamsaraVehicles";
import { LocationMapMarker } from "./LocationMapMarker";
import { TractorMapMarker } from "./TractorMapMarker";
import { ThemedTileLayer } from "./ThemedTileLayer";
import { isVehicleStale } from "@/lib/samsara-staleness";

/** Visual radius for YMS location circles on the map (meters). */
const YMS_CIRCLE_RADIUS = 200;

interface AtlasMapProps {
  locations: AtlasLocation[];
  selectedLocationId?: string | null;
  liveVehicles?: SamsaraVehicle[];
  selectedVehicle?: SamsaraVehicle | null;
  openPopupVehicleId?: string | null;
  vehicleCapabilityFilter?: "all" | "VO" | "NVO" | "Manual";
  ymsLocations?: AtlasLocation[];
  onResetViewReady?: (resetFn: () => void) => void;
}

function FlyToLocation({ locations, selectedLocationId }: { locations: AtlasLocation[]; selectedLocationId?: string | null }) {
  const map = useMap();

  useEffect(() => {
    if (!selectedLocationId) return;
    const loc = locations.find((l) => l.id === selectedLocationId);
    if (loc) {
      map.flyTo([loc.lat, loc.lng], 10, { duration: 1.2, easeLinearity: 0.25 });
    }
  }, [selectedLocationId, locations, map]);

  return null;
}

function FlyToVehicle({ vehicle }: { vehicle: SamsaraVehicle | null | undefined }) {
  const map = useMap();

  useEffect(() => {
    if (!vehicle || vehicle.lat == null || vehicle.lng == null) return;
    map.flyTo([vehicle.lat, vehicle.lng], 12, { duration: 1.2, easeLinearity: 0.25 });
  }, [vehicle, map]);

  return null;
}

const DEFAULT_ZOOM = 6;

function ResetViewBridge({ onReady }: { onReady?: (fn: () => void) => void }) {
  const map = useMap();
  const reset = useCallback(() => {
    map.flyTo(DEFAULT_CENTER, DEFAULT_ZOOM, { duration: 1.2, easeLinearity: 0.25 });
  }, [map]);
  useEffect(() => { onReady?.(reset); }, [onReady, reset]);
  return null;
}

// Default center over the continental US — used as stable initial center
const DEFAULT_CENTER: [number, number] = [31.5, -99.0];

export function AtlasMap({ locations, selectedLocationId, liveVehicles = [], selectedVehicle, openPopupVehicleId = null, vehicleCapabilityFilter = "all", ymsLocations = [], onResetViewReady }: AtlasMapProps) {
  return (
    <MapContainer
      center={DEFAULT_CENTER}
      zoom={DEFAULT_ZOOM}
      className="h-full w-full rounded-lg"
      style={{ minHeight: "calc(100vh - 10rem)" }}
    >
      <ThemedTileLayer />
      <ResetViewBridge onReady={onResetViewReady} />
      <FlyToLocation locations={locations} selectedLocationId={selectedLocationId} />
      <FlyToVehicle vehicle={selectedVehicle} />
      {ymsLocations.map((loc) => (
        <Circle
          key={`yms-${loc.id}`}
          center={[loc.lat, loc.lng]}
          radius={YMS_CIRCLE_RADIUS}
          pathOptions={{
            color: "#3b82f6",
            fillColor: "#3b82f6",
            fillOpacity: 0.15,
            weight: 1.5,
          }}
        />
      ))}
      {locations.map((loc) => (
        <LocationMapMarker key={`${loc.id}-${loc.drivers.length}-${loc.tractorCount}`} location={loc} />
      ))}
      {liveVehicles.map((vehicle) => {
        const caps = vehicle.capabilities && vehicle.capabilities.length > 0 ? vehicle.capabilities : ["Manual"];
        const dimmed = vehicleCapabilityFilter !== "all" && !caps.includes(vehicleCapabilityFilter);
        const stale = isVehicleStale(vehicle);
        return (
          <TractorMapMarker
            key={vehicle.id}
            tractor={{
              id: vehicle.id,
              name: vehicle.name,
              lat: vehicle.lat!,
              lng: vehicle.lng!,
              status: vehicle.status,
              currentCity: vehicle.currentCity,
              speed: vehicle.speed,
              heading: vehicle.heading,
              headingDeg: vehicle.headingDeg,
              driver: vehicle.driver,
              lastUpdate: vehicle.lastUpdate,
              nextStop: vehicle.nextStop,
              capabilities: vehicle.capabilities,
              trailerId: vehicle.trailerId,
              orderNumber: vehicle.orderNumber,
              orderId: vehicle.orderId,
            }}
            dimmed={dimmed}
            isStale={stale}
            forceOpen={openPopupVehicleId === vehicle.id}
          />
        );
      })}
    </MapContainer>
  );
}
